import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CalendarCheck, ChevronLeft, Loader2, MapPin, Scissors, User } from 'lucide-react';
import { ClientPortal } from './ClientPortal';

const API_URL = import.meta.env.VITE_API_URL || '';

const apiBase = API_URL ? `${API_URL}/api` : '/api';

// Resolution order: URL path param (/agendar/:tenantSlug) → ?tenant= query → build-time env → 'alpha' default.
function resolveTenantSlug(pathSlug?: string, querySlug?: string | null): string {
  return pathSlug || querySlug || import.meta.env.VITE_TENANT_SLUG || 'alpha';
}

interface Unit {
  id: string;
  name: string;
  address?: string | null;
}

interface Service {
  id: string;
  name: string;
  price: number;
  durationMin: number;
}

interface ServiceGroup {
  category: { id: string; name: string; color?: string | null } | null;
  services: Service[];
}

interface Barber {
  id: string;
  name: string;
  color: string;
  services: { service: Service }[];
}

interface Slot {
  time: string;
  barberId: string;
}

interface PublicServiceResponse {
  grouped: boolean;
  groups: ServiceGroup[];
}

type Step = 'unit' | 'services' | 'barber' | 'time' | 'client' | 'success';

function BookingApp() {
  const { tenantSlug: pathTenantSlug } = useParams<{ tenantSlug?: string }>();
  const [searchParams] = useSearchParams();
  const tenantSlug = useMemo(
    () => resolveTenantSlug(pathTenantSlug, searchParams.get('tenant')),
    [pathTenantSlug, searchParams]
  );
  const api = useMemo(
    () => axios.create({ baseURL: `${apiBase}/t/${tenantSlug}` }),
    [tenantSlug]
  );

  const [step, setStep] = useState<Step>('unit');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [units, setUnits] = useState<Unit[]>([]);
  const [serviceGroups, setServiceGroups] = useState<ServiceGroup[]>([]);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);

  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedBarber, setSelectedBarber] = useState<Barber | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [appointment, setAppointment] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .get('/public/units')
      .then((res) => setUnits(res.data.data))
      .catch(() => setError('Não foi possível carregar as unidades'))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    if (step === 'services' && serviceGroups.length === 0) {
      setLoading(true);
      api
        .get<{ data: PublicServiceResponse }>('/public/services')
        .then((res) => setServiceGroups(res.data.data.groups))
        .catch(() => setError('Não foi possível carregar os serviços'))
        .finally(() => setLoading(false));
    }
  }, [step, serviceGroups.length, api]);

  useEffect(() => {
    if (step === 'barber' && selectedUnit && selectedServices.length > 0 && barbers.length === 0) {
      setLoading(true);
      api
        .get<{ data: Barber[] }>('/public/barbers', {
          params: { unitId: selectedUnit.id, serviceIds: selectedServices.join(',') },
        })
        .then((res) => setBarbers(res.data.data))
        .catch(() => setError('Não foi possível carregar os barbeiros'))
        .finally(() => setLoading(false));
    }
  }, [step, selectedUnit, selectedServices, barbers.length, api]);

  useEffect(() => {
    if (step === 'time' && selectedUnit && selectedBarber && selectedDate) {
      setLoading(true);
      api
        .get<{ data: { slots: Slot[] } }>('/public/availability/slots', {
          params: {
            unitId: selectedUnit.id,
            barberId: selectedBarber.id,
            serviceIds: selectedServices.join(','),
            date: selectedDate,
          },
        })
        .then((res) => setSlots(res.data.data.slots))
        .catch(() => setError('Não foi possível carregar os horários'))
        .finally(() => setLoading(false));
    }
  }, [step, selectedUnit, selectedBarber, selectedDate, selectedServices, api]);

  const selectedServiceDetails = useMemo(() => {
    const all: Service[] = serviceGroups.flatMap((g) => g.services);
    return all.filter((s) => selectedServices.includes(s.id));
  }, [serviceGroups, selectedServices]);

  const totalDuration = useMemo(
    () => selectedServiceDetails.reduce((sum, s) => sum + s.durationMin, 0),
    [selectedServiceDetails]
  );

  const totalPrice = useMemo(
    () => selectedServiceDetails.reduce((sum, s) => sum + Number(s.price), 0),
    [selectedServiceDetails]
  );

  function toggleService(id: string) {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  async function submitBooking() {
    if (!selectedUnit || !selectedBarber || !selectedSlot) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/public/appointments', {
        unitId: selectedUnit.id,
        barberId: selectedBarber.id,
        serviceIds: selectedServices,
        scheduledAt: selectedSlot,
        clientName,
        clientPhone,
      });
      setAppointment(res.data.data);
      setStep('success');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        setError(err.response.data.error.message || 'Erro ao agendar');
      } else {
        setError('Erro ao confirmar agendamento');
      }
    } finally {
      setLoading(false);
    }
  }

  function formatTime(iso: string) {
    const d = new Date(iso);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  }

  function formatPrice(value: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  function renderHeader() {
    return (
      <div className="mb-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-600 text-white">
          <CalendarCheck className="h-7 w-7" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-slate-900">Agende seu horário</h1>
        <p className="mt-1 text-sm text-slate-500">Escolha a unidade, serviços e horário</p>
        <p className="mt-2 text-xs text-slate-400">Barbearia · {tenantSlug}</p>
      </div>
    );
  }

  function renderError() {
    if (!error) return null;
    return (
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  function renderLoading() {
    if (!loading) return null;
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (loading && step === 'unit') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="mx-auto max-w-md">
        {renderHeader()}
        {renderError()}

        {step === 'unit' && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Selecione a unidade
            </h2>
            {units.map((unit) => (
              <button
                key={unit.id}
                onClick={() => {
                  setSelectedUnit(unit);
                  setStep('services');
                  setError(null);
                }}
                className="flex w-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-emerald-500 hover:shadow-md"
              >
                <MapPin className="mt-0.5 h-5 w-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-slate-900">{unit.name}</p>
                  {unit.address && <p className="text-sm text-slate-500">{unit.address}</p>}
                </div>
              </button>
            ))}
          </div>
        )}

        {step === 'services' && (
          <div className="space-y-4">
            <button
              onClick={() => setStep('unit')}
              className="flex items-center gap-1 text-sm font-medium text-emerald-700"
            >
              <ChevronLeft className="h-4 w-4" /> Voltar
            </button>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Selecione os serviços
            </h2>
            {serviceGroups.map((group) => (
              <div key={group.category?.id ?? 'uncategorized'} className="space-y-2">
                <p className="text-xs font-semibold text-slate-400">
                  {group.category?.name ?? 'Sem categoria'}
                </p>
                <div className="space-y-2">
                  {group.services.map((service) => {
                    const selected = selectedServices.includes(service.id);
                    return (
                      <button
                        key={service.id}
                        onClick={() => toggleService(service.id)}
                        className={`flex w-full items-center justify-between rounded-xl border p-4 text-left transition ${
                          selected
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-slate-200 bg-white hover:border-emerald-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Scissors className="h-5 w-5 text-emerald-600" />
                          <div>
                            <p className="font-medium text-slate-900">{service.name}</p>
                            <p className="text-xs text-slate-500">{service.durationMin} min</p>
                          </div>
                        </div>
                        <span className="font-semibold text-slate-900">
                          {formatPrice(Number(service.price))}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {selectedServices.length > 0 && (
              <button
                onClick={() => {
                  setBarbers([]);
                  setStep('barber');
                  setError(null);
                }}
                className="w-full rounded-xl bg-emerald-600 py-3 font-semibold text-white shadow transition hover:bg-emerald-700"
              >
                Continuar ({formatPrice(totalPrice)} · {totalDuration} min)
              </button>
            )}
          </div>
        )}

        {step === 'barber' && (
          <div className="space-y-4">
            <button
              onClick={() => setStep('services')}
              className="flex items-center gap-1 text-sm font-medium text-emerald-700"
            >
              <ChevronLeft className="h-4 w-4" /> Voltar
            </button>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Escolha o profissional e a data
            </h2>
            {renderLoading()}
            <div className="space-y-2">
              {barbers.map((barber) => (
                <button
                  key={barber.id}
                  onClick={() => setSelectedBarber(barber)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition ${
                    selectedBarber?.id === barber.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 bg-white hover:border-emerald-300'
                  }`}
                >
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: barber.color }}
                  >
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{barber.name}</p>
                    <p className="text-xs text-slate-500">
                      {barber.services.map((s) => s.service.name).join(', ')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            {selectedBarber && (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Data</label>
                <input
                  type="date"
                  value={selectedDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedSlot(null);
                    setSlots([]);
                    setError(null);
                  }}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </div>
            )}
            {selectedDate && (
              <button
                onClick={() => {
                  setStep('time');
                  setError(null);
                }}
                disabled={slots.length === 0 && !loading}
                className="w-full rounded-xl bg-emerald-600 py-3 font-semibold text-white shadow transition hover:bg-emerald-700 disabled:opacity-50"
              >
                Ver horários
              </button>
            )}
          </div>
        )}

        {step === 'time' && (
          <div className="space-y-4">
            <button
              onClick={() => setStep('barber')}
              className="flex items-center gap-1 text-sm font-medium text-emerald-700"
            >
              <ChevronLeft className="h-4 w-4" /> Voltar
            </button>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Selecione o horário
            </h2>
            {renderLoading()}
            {slots.length === 0 && !loading && (
              <p className="text-center text-sm text-slate-500">
                Nenhum horário disponível para essa data.
              </p>
            )}
            <div className="grid grid-cols-3 gap-2">
              {slots.map((slot) => (
                <button
                  key={slot.time}
                  onClick={() => setSelectedSlot(slot.time)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    selectedSlot === slot.time
                      ? 'border-emerald-500 bg-emerald-600 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-400'
                  }`}
                >
                  {formatTime(slot.time)}
                </button>
              ))}
            </div>
            {selectedSlot && (
              <button
                onClick={() => {
                  setStep('client');
                  setError(null);
                }}
                className="w-full rounded-xl bg-emerald-600 py-3 font-semibold text-white shadow transition hover:bg-emerald-700"
              >
                Continuar
              </button>
            )}
          </div>
        )}

        {step === 'client' && (
          <div className="space-y-4">
            <button
              onClick={() => setStep('time')}
              className="flex items-center gap-1 text-sm font-medium text-emerald-700"
            >
              <ChevronLeft className="h-4 w-4" /> Voltar
            </button>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Seus dados
            </h2>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Nome</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Seu nome completo"
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">WhatsApp</label>
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="(11) 99999-9999"
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              <p>
                <strong>Unidade:</strong> {selectedUnit?.name}
              </p>
              <p>
                <strong>Profissional:</strong> {selectedBarber?.name}
              </p>
              <p>
                <strong>Data/hora:</strong>{' '}
                {selectedSlot ? formatTime(selectedSlot) : ''} · {selectedDate}
              </p>
              <p>
                <strong>Total:</strong> {formatPrice(totalPrice)} · {totalDuration} min
              </p>
            </div>
            <button
              onClick={submitBooking}
              disabled={!clientName || clientPhone.length < 10 || loading}
              className="w-full rounded-xl bg-emerald-600 py-3 font-semibold text-white shadow transition hover:bg-emerald-700 disabled:opacity-50"
            >
              {loading ? 'Confirmando...' : 'Confirmar agendamento'}
            </button>
          </div>
        )}

        {step === 'success' && appointment && (
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CalendarCheck className="h-8 w-8" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Agendamento confirmado!</h2>
            <p className="text-slate-600">
              Enviamos os detalhes para o WhatsApp {clientPhone}.<br />
              Obrigado por escolher nossa barbearia.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-xl border border-emerald-600 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
            >
              Novo agendamento
            </button>
          </div>
        )}

        <p className="mt-8 text-center text-xs text-slate-400">
          Barbearia: {tenantSlug} ·{' '}
          <Link to={`/${tenantSlug}/conta`} className="text-emerald-700 hover:underline">
            Minha conta
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter basename="/agendar">
      <Routes>
        <Route path="/:tenantSlug/conta" element={<ClientPortal />} />
        <Route path="/:tenantSlug/*" element={<BookingApp />} />
        <Route path="/" element={<BookingApp />} />
      </Routes>
    </BrowserRouter>
  );
}
